import { DOCUMENT_CATEGORY_LABELS } from './document-category-labels';

/**
 * German-canonical assistant notice strings (IMM-307.3). Keyed by the
 * `NoticeCode` union in `@immo/api-contract` (`assistant.run.*`); a client
 * renders these by code for a persisted NOTICE message. Register: informal `du`.
 * Per ADR 0009 the backend never holds German — it emits the code + an en-US
 * fallback, and this is the localized copy.
 */
export const notices = {
  assistant: {
    run: {
      'stopped-limit':
        'Diese Anfrage wurde zu umfangreich und ich musste abbrechen. Stell die Frage gerne enger gefasst noch einmal.',
      failed: 'Da ist etwas schiefgelaufen. Bitte versuche es noch einmal.',
    },
    'page-read': {
      'not-supported':
        'Ich kann im Moment nur Seiten auf ImmoScout24 lesen. Öffne die Seite dort und frag noch einmal.',
    },
  },
  // A caller's one-question message, sent over a messenger session (IMM-1097.1),
  // rendered only by `I18nWhatsAppCopy` — never in code.
  messenger: {
    'agent-question': {
      // Die Produkt-Kurzfassung des Tickets (OPS-87): die Nachricht beginnt damit, worum es im Ticket geht.
      brief: '📋 {ticket}: {brief}',
      heading: '🤖 {lane} bei {ticket} braucht eine Entscheidung:',
      option: '{number}. {text}',
      'option-recommended': '{number}. {text} (empfohlen)',
      'ticket-link': 'Ticket: {link}',
      'reply-with-options': 'Antworte mit der Nummer einer Option oder mit deiner eigenen Antwort.',
      'reply-without-options': 'Antworte einfach mit deiner Entscheidung.',
      link: 'Auch auf der Fleet-Seite: {link}',
      chosen: 'Gewählt: {key}',
      'answered-elsewhere': 'Beantwortet in der Admin-App: {answer}',
      'answered-in-chat': 'Im Chat eingetragen: {answer}',
      withdrawn: 'Nicht mehr offen.',
    },
    // Die festen Antworten des Fleet-Assistenten (IMM-1420); freie Antworten gehen unverändert hinaus.
    'fleet-assistant': {
      'asks-for-ticket': 'Um welches Ticket geht es? Offen sind gerade: {tickets}',
      'asks-for-ticket-none': 'Um welches Ticket geht es? Gerade ist kein Job offen.',
      refused:
        'Das kann ich nicht für dich tun. Ich kann dir alles zur Fleet beantworten und Antworten auf offene Fragen eintragen – alles andere geht auf der Fleet-Seite.',
      failed:
        'Ich kann gerade nicht antworten. Versuch es gleich noch einmal – die Antwort-Buttons funktionieren weiter.',
      'answer-recorded': 'Eingetragen: Für {ticket} gilt jetzt „{answer}“.',
      'already-answered': '{ticket} ist schon beantwortet: „{answer}“ ({name}). Ich habe nichts geändert.',
    },
    // Einseitige Fleet-Hinweise (OPS-62): es wird keine Antwort erwartet.
    'fleet-job-parked': {
      heading: '🚨 {lane} hat {ticket} geparkt und braucht einen Menschen.',
      'ticket-link': 'Ticket: {link}',
      reason: 'Grund: {reason}',
      link: 'Fleet-Seite: {link}',
    },
    'fleet-worker-silent': {
      job: '🛑 Worker {worker} auf Host {host} meldet sich nicht mehr, während er an {ticket} arbeitet.',
      released: 'Der Job wurde freigegeben, damit ein anderer Worker das Ticket übernehmen kann.',
      link: 'Fleet-Seite: {link}',
    },
    'fleet-release-blocked': {
      heading: '🚧 Release {version} ist blockiert (gemeldet von {machine}).',
      manual: 'Das Deployment lief nicht. Nach der Behebung der Ursache /feature-deploy von Hand ausführen.',
    },
    // Eine Frage, die ihr Fenster lang unbeantwortet blieb, kommt später wieder (OPS-79).
    'still-open': '⏳ Noch offen seit {time}:',
    // Die Antwort auf `/pending` (OPS-79).
    pending: {
      heading: 'Offene Fragen ({count}), älteste zuerst:',
      item: '{number}. {ticket}: {headline}',
      'item-open': '{number}. {ticket}: {headline} (deine nächste Antwort geht hierhin)',
      footer: 'Zitiere die Nachricht einer Frage, um sie zu beantworten. Die anderen kommen der Reihe nach wieder.',
      empty: 'Keine offenen Fragen.',
    },
    'quoted-not-open': 'Diese Frage ist nicht mehr offen. /pending zeigt die offenen Fragen.',
  },
  // The fixed replies immoOS sends over Telegram (IMM-1128.1, IMM-1127.2), rendered
  // only by `I18nTelegramCopy` — never in code.
  telegram: {
    'assistant-moved':
      'Der immoOS-Assistent ist umgezogen. Schreib ihm ab jetzt hier: @immoos_bot – und verbinde dein Konto bei Bedarf neu in deinem immoOS-Profil.',
    'unknown-sender':
      'Hallo! Du hast den immoOS-Makler-Assistenten auf Telegram erreicht. Du bist noch nicht als Makler bei immoOS registriert. Bitte wende dich an unseren Vertrieb: info@immoos.ai',
    link: {
      welcome:
        'Dein Telegram-Konto ist jetzt mit immoOS verbunden, {firstName}. Frag mich einfach, was du wissen willst.',
      'welcome-without-name':
        'Dein Telegram-Konto ist jetzt mit immoOS verbunden. Frag mich einfach, was du wissen willst.',
      'account-taken': 'Dieses Telegram-Konto ist bereits mit einem anderen immoOS-Konto verbunden.',
    },
  },
  // The fixed WhatsApp replies immoOS sends to a member's own number
  // (IMM-1040.1/.2/.5 — and the one reply to an unknown number), rendered
  // only by `I18nWhatsAppCopy` — never in code.
  whatsapp: {
    link: {
      welcome: 'Deine Nummer ist jetzt mit immoOS verbunden, {firstName}. Frag mich einfach, was du wissen willst.',
      'welcome-without-name': 'Deine Nummer ist jetzt mit immoOS verbunden. Frag mich einfach, was du wissen willst.',
      'invalid-code': 'Der Code stimmt nicht oder ist abgelaufen. Hol dir in deinem immoOS-Profil einen neuen Code.',
      'number-taken': 'Diese WhatsApp-Nummer ist bereits mit einem anderen immoOS-Konto verbunden.',
    },
    'media-not-supported': 'Ich kann im Moment nur Textnachrichten lesen. Schreib mir bitte als Text.',
    'unknown-sender':
      'Hallo! Du hast die Nummer des immoOS-Makler-Assistenten erreicht. Du bist noch nicht als Makler bei immoOS registriert. Bitte wende dich an unseren Vertrieb: info@immoos.ai',
    confirm: {
      // The one word `isAffirmative` accepts (IMM-1040.2) — the same word the
      // IMM-1040.3 confirmation question tells the member to send.
      affirmative: 'Ja',
      question: 'Soll ich das so senden? Antworte mit „Ja“, dann schicke ich es ab.',
      'document-request':
        'Ich würde diese Dokumentenanfrage an den Eigentümer schicken:\nObjekt: {property}\nUnterlagen: {categories}\nNotiz: {note}',
      'document-request-no-note':
        'Ich würde diese Dokumentenanfrage an den Eigentümer schicken:\nObjekt: {property}\nUnterlagen: {categories}',
      'viewing-invitation':
        'Ich würde diese Einladung zur Besichtigung per E-Mail verschicken:\nInteressent: {prospect}\nTermin: {date}, {start}–{end} Uhr\nHinweis: {hint}',
      'viewing-invitation-no-hint':
        'Ich würde diese Einladung zur Besichtigung per E-Mail verschicken:\nInteressent: {prospect}\nTermin: {date}, {start}–{end} Uhr',
      // Referenced, not copied (D6): the same map `pages.ts` uses for the
      // document tags + picker.
      category: DOCUMENT_CATEGORY_LABELS,
    },
  },
} as const;
